// Placeholder simple para rutas, tarifas, addons y reservas del admin
// El componente AdminVehicles ya sirve como referencia completa

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Ban, Eye, X, MapPin, Calendar, Users, Phone, Mail, User, FileText, CreditCard, Clock, Plus, Plane, Luggage } from 'lucide-react';
import { AdminPagination } from '../../components/AdminPagination';
import { AdminSearchBar } from '../../components/AdminSearchBar';

export const AdminRoutes = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await adminApi.getRoutes(page, 20, search);
      setItems(res.data.items);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Error cargando datos');
    }
  }, [page, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Rutas</h1>
        <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar ruta..." />
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <p className="text-slate-600 mb-4">Total de rutas: {total}</p>
        {items.map(r => (
          <div key={r.id} className="py-2 border-t border-slate-100 text-sm">
            {r.from_label} → {r.to_label} {!r.is_active && <span className="text-red-500">(inactiva)</span>}
          </div>
        ))}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const AdminRates = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await adminApi.getRates(page, 20, search);
      setItems(res.data.items);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Error cargando datos');
    }
  }, [page, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Tarifas</h1>
        <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar tarifa..." />
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <p className="text-slate-600 mb-4">Total de tarifas: {total}</p>
        {items.map(r => (
          <div key={r.id} className="py-2 border-t border-slate-100 text-sm">
            Ruta: {r.route_id} | Vehículo: {r.vehicle_type_id} | Precio: ${r.base_price}
          </div>
        ))}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const AdminAddons = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await adminApi.getAddons(page, 20, search);
      setItems(res.data.items);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Error cargando datos');
    }
  }, [page, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Extras</h1>
        <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar extra..." />
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <p className="text-slate-600 mb-4">Total de extras: {total}</p>
        {items.map(a => (
          <div key={a.id} className="py-2 border-t border-slate-100 text-sm">
            {a.name} — ${a.price} ({a.type}) {!a.is_active && <span className="text-red-500">(inactivo)</span>}
          </div>
        ))}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', bg: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' },
  paid: { label: 'Pagada', bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  confirmed: { label: 'Confirmada', bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  rejected: { label: 'Rechazada', bg: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  cancelled: { label: 'Cancelada', bg: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${config.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
      {config.label}
    </span>
  );
};

export const AdminReservations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(null);
  const [reason, setReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [rates, setRates] = useState([]);
  const [newReservation, setNewReservation] = useState({
    customer_name: '', customer_email: '', customer_phone: '', flight_number: '', luggage_count: 0,
    from_label: '', to_label: '', vehicle_type_id: '',
    pickup_at: '', passengers: 1, is_roundtrip: false, return_at: '',
    pickup_address: '', pickup_exact_address: '', dropoff_exact_address: '', notes: '', total_override: ''
  });

  const loadReservations = useCallback(async (p = page, status = statusFilter) => {
    try {
      setLoading(true);
      const apiStatus = status === 'all' ? '' : status;
      const res = await adminApi.getReservations(p, 20, apiStatus, search);
      setItems(res.data.items);
      setTotalPages(res.data.total_pages);
      setTotalCount(res.data.total);
      if (res.data.status_counts) {
        setStatusCounts(res.data.status_counts);
      }
    } catch (err) {
      toast.error('Error cargando reservas');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [page, statusFilter, search]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  const loadFormData = async () => {
    try {
      const [routesRes, vehiclesRes, ratesRes] = await Promise.all([
        adminApi.getRoutes(1, 100), adminApi.getVehicles(1, 100), adminApi.getRates(1, 100)
      ]);
      setRoutes((routesRes.data.items || []).filter(r => r.is_active !== false));
      setVehicles((vehiclesRes.data.items || []).filter(v => v.is_active !== false));
      setRates((ratesRes.data.items || []).filter(r => r.is_active !== false));
    } catch (err) {
      toast.error('Error cargando datos del formulario');
    }
  };

  useEffect(() => {
    loadReservations(page, statusFilter);
  }, [page, statusFilter, loadReservations]);
  
  const getCalculatedPrice = () => {
    if (newReservation.total_override) return parseFloat(newReservation.total_override);
    const selectedRoute = routes.find(r => r.from_label === newReservation.from_label && r.to_label === newReservation.to_label);
    if (!selectedRoute || !newReservation.vehicle_type_id) return null;
    const rate = rates.find(r => r.route_id === selectedRoute.id && r.vehicle_type_id === newReservation.vehicle_type_id);
    if (!rate) return null;
    let price = rate.base_price;
    if (newReservation.is_roundtrip) price = price * 1.8;
    return price;
  };
  
  const openCreateModal = async () => {
    await loadFormData();
    setNewReservation({
      customer_name: '', customer_email: '', customer_phone: '', flight_number: '', luggage_count: 0,
      from_label: '', to_label: '', vehicle_type_id: '',
      pickup_at: '', passengers: 1, is_roundtrip: false, return_at: '',
      pickup_address: '', pickup_exact_address: '', dropoff_exact_address: '', notes: '', total_override: ''
    });
    setShowCreateModal(true);
  };
  
  const handleCreateReservation = async () => {
    const { customer_name, customer_email, customer_phone, from_label, to_label, vehicle_type_id, pickup_at, passengers, is_roundtrip, return_at } = newReservation;
    if (!customer_name || !customer_email || !customer_phone || !from_label || !to_label || !vehicle_type_id || !pickup_at || !passengers) {
      toast.error('Complete todos los campos obligatorios');
      return;
    }
    if (is_roundtrip && !return_at) {
      toast.error('Seleccione la fecha y hora de regreso para viaje ida y vuelta');
      return;
    }
    setCreateLoading(true);
    try {
      const payload = {
        ...newReservation,
        passengers: parseInt(newReservation.passengers),
        total_override: newReservation.total_override ? parseFloat(newReservation.total_override) : null
      };
      await adminApi.createReservation(payload);
      toast.success('Reserva creada exitosamente');
      setShowCreateModal(false);
      await loadReservations(page, statusFilter);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al crear la reserva';
      toast.error(detail);
    } finally {
      setCreateLoading(false);
    }
  };
  
  const updateField = (field, value) => {
    setNewReservation(prev => ({ ...prev, [field]: value }));
  };
  
  const handleRouteSelect = (routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (route) {
      setNewReservation(prev => ({ ...prev, from_label: route.from_label, to_label: route.to_label }));
    }
  };
  
  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    setPage(1);
  };

  const handleStatusUpdate = async (reservationId, newStatus, statusReason = '') => {
    setActionLoading(true);
    try {
      await adminApi.updateReservationStatus(reservationId, newStatus, statusReason);
      const labels = { confirmed: 'aceptada', rejected: 'rechazada', cancelled: 'cancelada' };
      toast.success(`Reserva ${labels[newStatus] || 'actualizada'} exitosamente`);
      setSelectedReservation(null);
      setShowReasonModal(null);
      setReason('');
      await loadReservations(page, statusFilter);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al actualizar';
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };
  
  const confirmReasonAction = () => {
    if (!selectedReservation || !showReasonModal) return;
    handleStatusUpdate(selectedReservation.reservation_id, showReasonModal, reason);
  };
  
  const allTotal = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  
  if (loading && !hasLoadedRef.current) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-manrope font-bold text-3xl text-emerald-950">Gestion de Reservas</h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <AdminSearchBar value={search} onChange={handleSearch} placeholder="Buscar reserva..." />
          <button
            onClick={openCreateModal}
            className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold
              rounded-lg transition-colors flex items-center gap-2 text-sm shrink-0"
          >
            <Plus className="w-4 h-4" />
            Nueva
          </button>
        </div>
      </div>
      
      {/* Status summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[['all', 'Todas', allTotal, 'bg-slate-600'],
          ['paid', 'Por aprobar', statusCounts.paid || 0, 'bg-blue-600'],
          ['confirmed', 'Confirmadas', statusCounts.confirmed || 0, 'bg-emerald-600'],
          ['rejected', 'Rechazadas', statusCounts.rejected || 0, 'bg-red-600'],
          ['cancelled', 'Canceladas', statusCounts.cancelled || 0, 'bg-orange-600'],
        ].map(([key, label, count, color]) => (
          <button
            key={key}
            onClick={() => handleStatusFilterChange(key)}
            className={`p-3 rounded-xl border text-left transition-all ${
              statusFilter === key 
                ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200' 
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2.5 h-2.5 rounded-full ${color}`}></span>
              <span className="text-xs font-medium text-slate-600">{label}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{count}</p>
          </button>
        ))}
      </div>
      
      {/* Table */}
      <div className={`bg-white rounded-xl shadow-sm border p-6 overflow-x-auto transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        {items.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No hay reservas {statusFilter !== 'all' ? `con estado "${STATUS_CONFIG[statusFilter]?.label}"` : ''}</p>
        ) : (
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-semibold">ID</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Cliente</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Ruta</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Fecha</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Total</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Estado</th>
              <th className="text-left py-3 px-4 text-sm font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.reservation_id} className="border-t hover:bg-slate-50">
                <td className="py-3 px-4 text-sm font-mono">{item.reservation_id.substring(0, 8)}</td>
                <td className="py-3 px-4 text-sm">{item.customer.full_name}</td>
                <td className="py-3 px-4 text-sm">{item.from_label} → {item.to_label}</td>
                <td className="py-3 px-4 text-sm">{new Date(item.pickup_at).toLocaleDateString()}</td>
                <td className="py-3 px-4 text-sm font-semibold">${item.total}</td>
                <td className="py-3 px-4 text-sm">
                  <StatusBadge status={item.status} />
                </td>
                <td className="py-3 px-4 text-sm">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedReservation(item)}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors"
                      title="Ver detalles"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    
                    {item.status === 'paid' && (
                      <>
                        <button
                          onClick={() => handleStatusUpdate(item.reservation_id, 'confirmed')}
                          disabled={actionLoading}
                          className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600 hover:text-emerald-700 transition-colors"
                          title="Aceptar reserva"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedReservation(item); setShowReasonModal('rejected'); }}
                          disabled={actionLoading}
                          className="p-1.5 rounded-lg hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors"
                          title="Rechazar reserva"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    
                    {item.status === 'confirmed' && (
                      <button
                        onClick={() => { setSelectedReservation(item); setShowReasonModal('cancelled'); }}
                        disabled={actionLoading}
                        className="p-1.5 rounded-lg hover:bg-orange-100 text-orange-600 hover:text-orange-700 transition-colors"
                        title="Cancelar reserva"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Detail Modal */}
      {selectedReservation && !showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReservation(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-manrope font-bold text-xl text-emerald-950">Detalle de Reserva</h3>
              <button onClick={() => setSelectedReservation(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500 font-mono">{selectedReservation.reservation_id}</span>
                <StatusBadge status={selectedReservation.status} />
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Ruta</p>
                    <p className="text-sm font-medium">{selectedReservation.from_label} → {selectedReservation.to_label}</p>
                  </div>
                </div>
                {(selectedReservation.pickup_exact_address || selectedReservation.pickup_address) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Recogida exacta</p>
                      <p className="text-sm font-medium">{selectedReservation.pickup_exact_address || selectedReservation.pickup_address}</p>
                    </div>
                  </div>
                )}
                {selectedReservation.dropoff_exact_address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Destino exacto</p>
                      <p className="text-sm font-medium">{selectedReservation.dropoff_exact_address}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Fecha y hora</p>
                    <p className="text-sm font-medium">{new Date(selectedReservation.pickup_at).toLocaleString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Pasajeros</p>
                    <p className="text-sm font-medium">{selectedReservation.passengers}</p>
                  </div>
                </div>
                {selectedReservation.is_roundtrip && (
                  <>
                    <p className="text-sm text-emerald-700 font-medium">Ida y vuelta</p>
                    {selectedReservation.return_at && (
                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Fecha de regreso</p>
                          <p className="text-sm font-medium">{new Date(selectedReservation.return_at).toLocaleString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-500" /><span className="text-sm">{selectedReservation.customer.full_name}</span></div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-500" /><span className="text-sm">{selectedReservation.customer.email}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-500" /><span className="text-sm">{selectedReservation.customer.phone}</span></div>
                <div className="flex items-center gap-2"><Plane className="w-4 h-4 text-slate-500" /><span className="text-sm">{selectedReservation.flight_number || 'No especificado'}</span></div>
                <div className="flex items-center gap-2"><Luggage className="w-4 h-4 text-slate-500" /><span className="text-sm">{selectedReservation.luggage_count ?? 0} maleta{selectedReservation.luggage_count === 1 ? '' : 's'}</span></div>
              </div>
              
              {selectedReservation.notes && (
                <div className="bg-amber-50 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Notas del cliente</p>
                      <p className="text-sm">{selectedReservation.notes}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-emerald-50 rounded-xl p-4 space-y-2">
                {selectedReservation.base_price > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Traslado base {selectedReservation.is_roundtrip ? '(ida y vuelta)' : ''}</span>
                    <span className="font-medium text-slate-800">${Number(selectedReservation.base_price).toFixed(2)}</span>
                  </div>
                )}
                {selectedReservation.addons && selectedReservation.addons.length > 0 && selectedReservation.addons.map((addon, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{addon.name || addon.addon_id} x{addon.quantity}</span>
                    <span className="font-medium text-slate-800">{addon.price ? `$${(addon.price * addon.quantity).toFixed(2)}` : '--'}</span>
                  </div>
                ))}
                {(selectedReservation.base_price > 0 || (selectedReservation.addons && selectedReservation.addons.length > 0)) && (
                  <div className="border-t border-emerald-200 pt-2"></div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Total</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600">${Number(selectedReservation.total).toFixed(2)} USD</span>
                </div>
              </div>
              
              {selectedReservation.status_reason && (
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-xs text-red-600 font-medium mb-1">Motivo</p>
                  <p className="text-sm text-red-800">{selectedReservation.status_reason}</p>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="p-6 border-t border-slate-200 space-y-2">
              {selectedReservation.status === 'paid' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStatusUpdate(selectedReservation.reservation_id, 'confirmed')}
                    disabled={actionLoading}
                    className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold 
                      rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {actionLoading ? 'Procesando...' : 'Aceptar reserva'}
                  </button>
                  <button
                    onClick={() => setShowReasonModal('rejected')}
                    disabled={actionLoading}
                    className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white font-semibold 
                      rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    Rechazar
                  </button>
                </div>
              )}
              
              {selectedReservation.status === 'confirmed' && (
                <button
                  onClick={() => setShowReasonModal('cancelled')}
                  disabled={actionLoading}
                  className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold 
                    rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Ban className="w-4 h-4" />
                  Cancelar reserva
                </button>
              )}
              
              <button
                onClick={() => setSelectedReservation(null)}
                className="w-full h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold 
                  rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reason Modal */}
      {showReasonModal && selectedReservation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowReasonModal(null); setReason(''); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <h3 className="font-manrope font-bold text-lg text-slate-900">
                {showReasonModal === 'rejected' ? '¿Rechazar esta reserva?' : '¿Cancelar esta reserva?'}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {showReasonModal === 'rejected' 
                  ? 'El cliente será notificado y deberás procesar el reembolso manualmente vía PayPal.' 
                  : 'La reserva confirmada será cancelada. Deberás procesar el reembolso manualmente vía PayPal.'}
              </p>
            </div>
            
            <div className="p-6">
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-sm"><strong>{selectedReservation.customer.full_name}</strong></p>
                <p className="text-xs text-slate-500">{selectedReservation.from_label} → {selectedReservation.to_label}</p>
                <p className="text-sm font-semibold text-emerald-600 mt-1">${selectedReservation.total} USD</p>
              </div>
              
              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Motivo (opcional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={showReasonModal === 'rejected' ? 'Ej: No hay disponibilidad para esa fecha...' : 'Ej: Solicitado por el cliente...'}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm 
                  focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
              />
            </div>
            
            <div className="p-6 border-t border-slate-200 flex gap-2">
              <button
                onClick={() => { setShowReasonModal(null); setReason(''); }}
                className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors"
              >
                Volver
              </button>
              <button
                onClick={confirmReasonAction}
                disabled={actionLoading}
                className={`flex-1 h-11 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                  showReasonModal === 'rejected' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {actionLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    {showReasonModal === 'rejected' ? <XCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    {showReasonModal === 'rejected' ? 'Rechazar' : 'Cancelar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Reservation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-manrope font-bold text-xl text-emerald-950">Nueva Reserva Manual</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
                Esta reserva se creara directamente como <strong>confirmada</strong>, sin pasar por el proceso de pago.
              </p>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Nombre del cliente *</label>
                <input type="text" value={newReservation.customer_name} onChange={e => updateField('customer_name', e.target.value)}
                  placeholder="Nombre completo" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Email *</label>
                  <input type="email" value={newReservation.customer_email} onChange={e => updateField('customer_email', e.target.value)}
                    placeholder="email@ejemplo.com" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Telefono *</label>
                  <input type="tel" value={newReservation.customer_phone} onChange={e => updateField('customer_phone', e.target.value)}
                    placeholder="+1 809 555 0000" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Ruta *</label>
                <select value={routes.find(r => r.from_label === newReservation.from_label && r.to_label === newReservation.to_label)?.id || ''}
                  onChange={e => handleRouteSelect(e.target.value)}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white">
                  <option value="">Seleccione una ruta</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.from_label} &rarr; {r.to_label}</option>)}
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Vehiculo *</label>
                <select value={newReservation.vehicle_type_id} onChange={e => updateField('vehicle_type_id', e.target.value)}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white">
                  <option value="">Seleccione un vehiculo</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} (hasta {v.max_passengers} pasajeros)</option>)}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Fecha y hora *</label>
                  <input type="datetime-local" value={newReservation.pickup_at} onChange={e => updateField('pickup_at', e.target.value)}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Pasajeros *</label>
                  <input type="number" min="1" max="20" value={newReservation.passengers} onChange={e => updateField('passengers', e.target.value)}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Dirección exacta de recogida</label>
                <input type="text" value={newReservation.pickup_exact_address} onChange={e => updateField('pickup_exact_address', e.target.value)}
                  placeholder="Ej: Hotel Barceló Bávaro Palace, Lobby principal" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Dirección exacta de destino</label>
                <input type="text" value={newReservation.dropoff_exact_address} onChange={e => updateField('dropoff_exact_address', e.target.value)}
                  placeholder="Ej: Aeropuerto Internacional Las Américas, Terminal A" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
              </div>
              
              <div className="flex items-center gap-2">
                <input type="checkbox" id="admin_roundtrip" checked={newReservation.is_roundtrip} onChange={e => updateField('is_roundtrip', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                <label htmlFor="admin_roundtrip" className="text-sm text-slate-700">Ida y vuelta</label>
              </div>
              
              {newReservation.is_roundtrip && (
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Fecha y hora de regreso *</label>
                  <input type="datetime-local" value={newReservation.return_at} onChange={e => updateField('return_at', e.target.value)}
                    min={newReservation.pickup_at}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Número de vuelo</label>
                  <input type="text" value={newReservation.flight_number} onChange={e => updateField('flight_number', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10))}
                    placeholder="Ej: AA1234" maxLength={10} className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Maletas</label>
                  <input type="number" min="0" max="20" value={newReservation.luggage_count} onChange={e => updateField('luggage_count', e.target.value)}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Notas</label>
                <textarea value={newReservation.notes} onChange={e => updateField('notes', e.target.value)}
                  placeholder="Notas adicionales..." rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none" />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Precio personalizado (USD)</label>
                <input type="number" step="0.01" min="0" value={newReservation.total_override} onChange={e => updateField('total_override', e.target.value)}
                  placeholder={getCalculatedPrice() ? `Calculado: $${getCalculatedPrice().toFixed(2)}` : 'Se calculara automaticamente'}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" />
                {getCalculatedPrice() && !newReservation.total_override && (
                  <p className="text-xs text-slate-500 mt-1">Precio calculado: ${getCalculatedPrice().toFixed(2)} USD</p>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex gap-2">
              <button onClick={() => setShowCreateModal(false)}
                className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors">
                Cancelar
              </button>
              <button onClick={handleCreateReservation} disabled={createLoading}
                className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold 
                  rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {createLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <><Plus className="w-4 h-4" /> Crear reserva</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
